
import { Component, Prop } from '@modules/vue-property-decorator';
import Vue from 'vue';
import { CheckoutPaymentData } from './checkoutPayment.vue';
import type { CheckoutParams } from './checkoutPayment.vue';
import { PaymentMethod, PaymentMethodType, TransbankPaymentQuery } from '@shared/core/constants';
import Container from '@shared/components/container/container.vue';
import { Loader } from '@shared/core/helpers/loaderHelper';
import { LocalStorage } from '@shared/core/localStorage';
import { ShopService } from '@shared/core/services/shopService';
import { CrudService } from '@shared/core/services/crudService';
import { Settings } from '@shared/core/services/configService';
import { Format } from '@shared/core/helpers/formatHelper';
import LoadScript from "@modules/vue-plugin-load-script";
import TriggerAnchor from '@shared/components/triggerAnchor/triggerAnchor.vue';
import Popup from '@shared/components/popup/popup-v2.vue';
import TransbankPM from '@shared/components/paymentMethods/transbankPM.vue';
import CreditCardForm from '@shared/components/form/creditCardForm.vue';
import SubmitButton from '@shared/components/submitButton/submitButton.vue';
import { DateHelper } from '@shared/core/helpers/dateHelper';

// @ts-ignore
Vue.use(LoadScript);

@Component({
  components: {
    Container,
    TriggerAnchor,
    Popup,
    TransbankPM,
    CreditCardForm,
    SubmitButton,
  },
})
export default class CheckoutPaymentMethods extends Vue {
  @Prop({ required: true }) private data!: CheckoutPaymentData|null;
  @Prop() private transport!: any;
  @Prop({ default: {promoCode: "", discount: 0, netDiscount: 0, discountedTaxes: 0} })
  private discountInfo!: {promoCode: string, discount: number, netDiscount: number, discountedTaxes: number};
  @Prop({ default: false }) private acceptConditions!: boolean;
  @Prop({ default: false }) private okRss!: boolean;

  private readonly PaymentMethodType = PaymentMethodType;
  private readonly shopService: ShopService;
  private readonly crudService: CrudService;
  private error: string|null = null;
  private paymentMethodId: number | null = null;
  private tokenId: number | null = null;
  private loading: boolean = false;
  private loadingText: string|null = null;
  private customer: any = null;
  private cid: any = null;
  
  private paypal: any = {};
  private paypalId: string = "";
  private paypalIsLoaded: boolean = false;
  private paypalPaymentMethod: any = undefined;
  private reloadPayButtonText: number = 0;

  private stripePaymentMethod: any = undefined;
  private showStripeForm: boolean = false;
  private currency: string = "EUR";
  private stripe: any;
  private elements: any;

  private netPayTokenInfo: any = null;
  private showCreditCardForm: boolean = false;
  private name: string = "";
  private cardnumber: string = "";
  private expirationdate: string = "";
  private securitycode: string = "";

  private showMercadopagoForm = false;
  private transbankTokenize = false;

  private showBridgerPayForm: boolean = false;

  constructor() {
    super();
    this.shopService = new ShopService();
    this.crudService = new CrudService();
  }

  private get isDarkMode() {
    return this.$store.state.settings.DarkMode === true;
  }

  // #region Computed
  private get total() {
    let total = this.$store.state.cart.total - this.discountInfo.discount;
    if (this.transport?.product) {
      total += this.transport.product.grossPrice;
    }
    return total;
  }
  private get requiresPayment() {
    return this.isFree === false;
  }

  private get requiresPaymentMethod() {
    // We need to have a card (saved or new) if:
    // 1. The purchase requires payment (not free)
    // 2. The user has no saved card and we need to save a new one
    return this.requiresPayment === true || this.requiresSavedCard === true;
  }

  private get isSavingCard() {
    return this.requiresToSaveNewCard === true && this.isFree === true;
  }

  private get isFree() {
    return this.total === 0;
  }
  private saveCreditCardCheck: boolean = false;
  private get saveCreditCard () {
    return this.saveCreditCardCheck || (this.paymentMethod?.forceSaveCreditCard === true || this.requiresToSaveNewCard === true);
  }
  private set saveCreditCard(value: boolean) {
    this.saveCreditCardCheck = value;
  }

  private get savedPaymentMethods() {
    return this.data?.paymentMethods.filter((p) => p.tokenId !== null);
  }

  private get notSavedPaymentMethods() {
    return this.data?.paymentMethods.filter((pMethod) => pMethod.tokenId == null);
  }

  private get paymentMethod() {
    return this.data?.paymentMethods.find(x => x.id === this.paymentMethodId && x.tokenId === this.tokenId);
  }

  private get userHasSavedCard() {
    return !!this.savedPaymentMethods?.length;
  }

  private get requiresSavedCard() {
    // We need a saved card if it's a shared payment without retention 
    // to pay for others later if they don't pay, so we have to save the card if the user doesn't have one
    return this.data?.isSharedPayment && this.data.withRetention === false;
  }

  private get requiresToSaveNewCard() {
    return this.requiresSavedCard === true && (this.userHasSavedCard === false || this.paymentMethod?.tokenId === null);
  }
  // #endregion Computed

  async mounted() {
    Loader.Loading("payment-methods", '');
    this.loading = true;
    this.loadingText = this.$t("Loading payment methods");
    const localStorageCheckoutParams = LocalStorage.get("checkoutParams");
    if (localStorageCheckoutParams) {
      LocalStorage.delete("checkoutParams");
      const pendingCheckoutParams = JSON.parse(
        localStorageCheckoutParams
      ) as CheckoutParams;
      const creditCardToken = await this.processTokenization();
      if (creditCardToken) {
        pendingCheckoutParams.tokenId = creditCardToken;
        await this.checkout(false, pendingCheckoutParams);
        Loader.Unloading("payment-methods");
        this.loading = false;
        return;
      }
    }

    let sid = LocalStorage.getWithTimeout("cc");
    if (sid !== undefined && sid !== null) {
      this.customer = JSON.parse(sid);
      this.cid = this.customer.id;
    }
    Loader.Unloading("payment-methods");
    this.loading = false;

    this.setUpPayPal();
  }
    
  private setCreditCardInfo(info: any) {
    this.error = "";
    this.name = info.name;
    this.cardnumber = info.cardnumber;
    this.expirationdate = info.expirationdate;
    this.securitycode = info.securitycode;
  }

  private showSaveCreditCard(pMethod: any) {
    return pMethod.allowTokenization === true && pMethod.tokenId === null;
  }

  private getPaymentMethodTypeImg(type: PaymentMethodType) {
    switch (type) {
      case PaymentMethodType.Redsys:
        return require('@/images/redsys.svg');
      case PaymentMethodType.Razorpay:
        return require('@/images/razorpay.svg');
      case PaymentMethodType.Transbank:
        return require('@/images/transbank.svg');
      case PaymentMethodType.MercadoPago:
        return require('@/images/mercado-pago.svg');
      case PaymentMethodType.Stripe:
        return require('@/images/stripe.svg');
      case PaymentMethodType.BridgerPay:
        return require('@/images/bridgerPay.svg');
      case PaymentMethodType.NetPay:
        return '/img/netpay.png';
      case PaymentMethodType.AuthorizeNet:
        return '/img/authorizenet.png';
      // case PaymentMethodType.Azul:
      //   return require('@/images/azul.svg');
      default:
        return null;
    }
    
  }

  private selectPaymentMethod(pm: PaymentMethod) {
    if (pm.type == PaymentMethodType.PayPal) {
      // Paypal is not selectable, just click on the provided button
      return;
    }
    this.paymentMethodId = pm.id;
    this.tokenId = pm.tokenId;
    this.$emit('payment-selected');
  }

  private saveCreditCardCheckChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this.saveCreditCard = target.checked;
  }

  // #region Payment methods

  /*** Paypal ***/

  private setUpPayPal() {
    this.paypalPaymentMethod = this.data?.paymentMethods
      .find((y) => y.type === PaymentMethodType.PayPal);

    if (this.paypalPaymentMethod) {
      this.paypalId = this.data!.payPalId;
      this.currency = this.data!.currency;
      this.loadPaypal();
    }
  }

  private loadPaypal() {
    if (!this.paypalIsLoaded) {
      this.createPayPalButtons();
      this.paypalIsLoaded = true;
    }
  }

  private createPayPalButtons() {
    const fake: any = this;
    fake
      .$loadScript(
        "https://www.paypal.com/sdk/js?client-id=" +
          this.paypalId +
          "&currency=" +
          this.currency +
          "&intent=authorize&disable-funding=mercadopago,credit,card",
        {
          "data-sdk-integration-source": "button-factory",
        }
      )
      .then(() => {
        this.paypal = window["paypal"];
        this.createPaypalContainerIfNotAvailable();
        const btns = this.paypal.Buttons({
          style: {
            shape: "rect",
            color: "gold",
            layout: "vertical",
            label: "paypal",
            size: "responsive",
          },
          onClick: () => {
            this.paymentMethodId = this.paypalPaymentMethod.id;
          },
          createOrder: async (data) => {
            return this.checkout(false);
          },
          onApprove: async (data, actions) => {
            // Authorize the transaction
            this.loading = true;
            this.loadingText = this.$t("Processing payment");
            actions.order
              .authorize()
              .then((authorization) => {
                // Get the authorization id
                var authorizationID =
                  authorization.purchase_units[0].payments.authorizations[0].id;

                // Call your server to validate and capture the transaction
                const url = `${Settings.HostName}/api/paypal/callback`;
                return this.crudService.httpPost(url, {
                  orderId: data.orderID,
                  authorizationId: authorizationID,
                });
              })
              .then((res) => {
                if (res === null) {
                  this.error = "Crash";
                  actions.close();
                  return;
                }
                if (res.ok === false) {
                  this.error = res.errorMsg;
                  console.error(res.errorMsg);
                  actions.close();
                  return;
                }
                if (res.ok === true) {
                  this.shopService.removeCart();
                  actions.redirect(
                    window.location.protocol +
                      "//" +
                      window.location.host +
                      "/cz/ok"
                  );
                }
              })
              .finally(() => {
                this.loading = false;
              });
          },
          onError: (err) => {
            console.error(err);
          },
        });
        setTimeout(function () {
          btns.render("#paypal-button-container");
        }, 3);
      });
  }

  private createPaypalContainerIfNotAvailable() {
    if (document.getElementById("paypal-button-container")) {
      return;
    } else {
      const superParent = document.getElementById("payPalParent");
      const parent = document.createElement("div");
      parent.setAttribute("id", "payPalButtons");
      superParent?.appendChild(parent);
      const child = document.createElement("div");
      child.setAttribute("id", "paypal-button-container");
      parent?.appendChild(child);
    }
  }

  /*** Stripe ***/

  private stripeSetUp(clientSecret: string, publicKey: string) {
    this.stripePaymentMethod = this.data?.paymentMethods.find(
      (y) => y.type === PaymentMethodType.Stripe
    );
    this.showStripeForm = true;

    if (this.stripePaymentMethod) {
      const fake: any = this;
      fake.$loadScript("https://js.stripe.com/v3/").then(() => {
        const Stripe = self.Stripe;
        fake.stripe = Stripe(publicKey);
        const appearance = {
          theme: this.isDarkMode ? "night" : "stripe",
        };
        fake.elements = fake.stripe.elements({ appearance, clientSecret });
        const paymentElementOptions = {
          layout: "tabs",
        };
        const paymentElement = fake.elements.create(
          "payment",
          paymentElementOptions
        );

        setTimeout(function () {
          paymentElement.mount("#payment-element");
        }, 1);
      });
    }
  }

  private async stripeCheckout() {
    let error: any = null;
    if(this.isSavingCard){
      const { e } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: document.location.origin + "/stripe-callback",
        },
      });
      error = e;
    }
    else {
      const { e } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: document.location.origin + "/stripe-callback",
        },
      });
      error = e;
    }

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      this.error = error.message;
    } else {
      this.error = this.$t("Crash").toString();
    }
  }

  // #region BridgerPay

  private closeBridgerPayForm() {
    this.showBridgerPayForm = false;
  }

  private bridgerPaySetUp(requestId: string, cashierToken: string, cashierKey: string) {
    this.showBridgerPayForm = true;
    this.$nextTick(() => {
      const paymentElement = document.getElementById("payment-element");
      const script = document.createElement("script");
      script.dataset.cashierKey = cashierKey;
      script.dataset.cashierToken = cashierToken;
      script.src = "https://checkout.bridgerpay.com/v2/launcher";
      const bridgerPayDepositListener = async (event: Event) => {
        window.removeEventListener(`[bp][cashier:${cashierKey}]:deposit`, bridgerPayDepositListener);
        if (event instanceof CustomEvent && event.detail.type === "declined") {
          return;
        }
        await this.bridgerPayCheckout(requestId, cashierToken);
      };
      window.addEventListener(`[bp][cashier:${cashierKey}]:deposit`, bridgerPayDepositListener);
      paymentElement?.appendChild(script);
    });
  }
  private async bridgerPayCheckout(requestId: string, cashierToken: string) {
    const url = `${Settings.HostName}/api/bridgerpay/process-payment/${requestId}?BP_TOKEN=${cashierToken}&saveCard=${this.saveCreditCard}`;
    const data = await new CrudService().httpPost(url);
    if (data == null || !data.ok) {
      // error
      return null;
    }
    this.closeBridgerPayForm();
    this.$router.push("/cz/ok");
  }
  // #endregion

  /***Authorizenet ***/
  private async authorizeNet() {
    await this.checkout(true);
  }

  /*** NetPay ***/
  private netPay() {
    const expiry = this.expirationdate.split("/");
    let cardInformation = {
      cardNumber: this.cardnumber,
      expMonth: expiry[0],
      expYear: expiry[1],
      cvv2: this.securitycode,
    };

    const fake: any = this;
    fake
      .$loadScript("https://docs.netpay.mx/cdn/v1.3/netpay.min.js")
      .then(() => {
        const NetPay = self.NetPay;

        NetPay.setApiKey(fake.data.netPayPublicKey);
        NetPay.setSandboxMode(fake.data.netPayProdMode === false);

        NetPay.card.validateNumber(cardInformation.cardNumber);
        NetPay.card.validateExpiry(
          cardInformation.expMonth,
          cardInformation.expYear
        );
        NetPay.card.validateCVV(
          cardInformation.cvv2,
          cardInformation.cardNumber
        );
        NetPay.card.validateNumberLength(cardInformation.cardNumber);

        NetPay.token.create(cardInformation, success, error);
        function success(e) {
          fake.netPayTokenInfo = JSON.parse(e.message.data);
          fake.netPayTokenInfo.ccv = cardInformation.cvv2;
          fake.checkout(true);
          // fake.showCreditCardForm = false;
        }

        function error(e) {
          fake.error = e.message;
        }
      });
  }

  /** MercadoPago ***/
  private mercadoPago(
    pk: string,
    id: number,
    amount: number,
    email: string,
    currency: string
  ) {
    const fake: any = this;
    this.showMercadopagoForm = true;
    setTimeout(() => {
      fake.$loadScript("https://sdk.mercadopago.com/js/v2").then(async () => {
        const MercadoPago = self.MercadoPago;
        const mp = new MercadoPago(pk, {
          locale: "es-AR",
        });
        const bricksBuilder = mp.bricks();

        const renderPaymentBrick = async (bricksBuilder) => {
          // Set up the payment preferences
          const settings = {
            initialization: {
              amount: amount,
              currency_id: currency,
              payer: {
                firstName: this.customer.name,
                lastName: "",
                email: email,
                entityType: "individual",
              },
            },
            customization: {
              visual: {
                style: {
                  theme: "default",
                },
              },
              paymentMethods: {
                debitCard: "all",
                // ticket: "all",
                // atm: "all",
                // onboarding_credits: "all",
                creditCard: "all",
                maxInstallments: 1, // if removed it shows periodic fees options
              },
              installments: 1,
            },
            callbacks: {
              onReady: () => {
                // Callback called when the Brick is ready.
                // You can hide loading indicators here, for example.
              },
              onSubmit: ({ selectedPaymentMethod, formData }) => {
                // Callback called when the submit button is clicked
                formData.rqId = id;
                formData.saveCardToken = this.saveCreditCard;
                const token = LocalStorage.getWithTimeout("ctk");
                const url =
                  process.env.NODE_ENV === "development"
                    ? "http://localhost:8819/api/publicmercadopago/process_payment"
                    : "/api/publicmercadopago/process_payment";
                this.loading = true;
                this.loadingText = this.$t("Processing payment");
                return new Promise((resolve, reject) => {
                  fetch(url, {
                    // taykusapi
                    method: "POST",
                    headers: {
                      Accept: "application/json",
                      Authorization: "Bearer " + token,
                      "content-type": "application/json-patch+json",
                    },
                    body: JSON.stringify(formData),
                  })
                    .then((response) => response.json())
                    .then((response) => {
                      // Receive the payment result
                      if (response.ok === true) {
                        this.$router.push("/cz/ok");
                        return;
                      }
                      this.showMercadopagoForm = false;
                      this.error = response.errorMsg;
                      resolve(true);
                    })
                    .catch((error) => {
                      this.error = error;
                      this.showMercadopagoForm = false;
                      reject();
                    })
                    .finally(() => {
                      this.loading = false;
                    });
                });
              },
              onError: (error) => {
                // Callback called for all Brick error cases
                371180303257522;
                this.error = error;
                this.showMercadopagoForm = false;
                console.error(error);
              },
            },
            saveCardToken: this.saveCreditCard, // Enable saving card data for future use
          };

          // Render the payment form inside the #payment-element div
          // @ts-ignore
          window.paymentBrickController = await bricksBuilder.create(
            "payment",
            "payment-element",
            settings
          );
        };

        renderPaymentBrick(bricksBuilder);
      });
    }, 100);
  }
  
  /*** Redsys ***/

  private redirectToRedsys(redsysData: any) {
    const data = redsysData.data;

    const form: any = this.$refs.redsysFrm;
    form.action = redsysData.url;

    const sVersion: any = this.$refs.ds_SignatureVersion;
    sVersion.value = data.ds_SignatureVersion;

    const sign: any = this.$refs.ds_Signature;
    sign.value = data.ds_Signature;

    const params: any = this.$refs.ds_MerchantParameters;
    params.value = data.ds_MerchantParameters;

    form.submit();
  }
  
  private openRazorpay(razorpayData: any) {
    const options = {
      key: razorpayData.apiKey,
      amount: razorpayData.amount,
      currency: razorpayData.currency,
      order_id: razorpayData.orderId,
      name: this.$store.state.settings.Title,
      prefill: {
          name: this.customer.name, 
          email: this.data?.email,
          contact: "+34699024587"
      },
      remember_customer: true,
      handler: function (response) {
        window.location.href = document.location.origin + `/razorpay-callback/?order_id=${razorpayData.orderId}&payment_id=${response.razorpay_payment_id}&signature=${response.razorpay_signature}`;
      }
    };
    const rzp = new (window as any).Razorpay(options);
    rzp.open();
  }

  /*** Transbank & Stripe ***/
  private async processTokenization() {
    if(this.$route.query.stripe_token) {
      return this.$route.query.stripe_token;
    }

    const transbankPaymentQuery = this.$route.query as TransbankPaymentQuery;
    const requestId = this.$route.query.tbkRqId;
    if (transbankPaymentQuery.TBK_TOKEN && requestId) {
      const payload = {
        requestId: requestId,
        token: transbankPaymentQuery.TBK_TOKEN,
      };
      if (
        transbankPaymentQuery.hasOwnProperty("TBK_ID_SESION") &&
        transbankPaymentQuery.hasOwnProperty("TBK_ORDEN_COMPRA")
      ) {
        this.error = this.$t("Save card failed");
        return;
      }
      const url = `${Settings.HostName}/api/transbank/process-tokenization/${payload.requestId}?TBK_TOKEN=${payload.token}`;
      const data = await this.crudService.httpPost(url);
      if (data == null) {
        this.error = this.$t("Crash").toString();
        return;
      }

      if (!data.ok) {
        this.error = data.errorMsg;
        return;
      }
      return data.model.id;
    }
  }

  private closeTransbank() {
    this.transbankTokenize = false;
  }

  private redirectToTransbank(transbankData: { url: string; token: string }) {
    const form = this.$refs.transbankForm! as HTMLFormElement;
    form.action = transbankData.url;

    const tbkToken = this.$refs.tbkToken! as HTMLInputElement;
    tbkToken.value = transbankData.token;

    form.submit();
  }
  // #endregion Payment methods
  private getPayButtonText() {
    const total = this.total;
    if (total > 0) {
      return (
        this.$t("Proceed to Pay").toString() + " " + Format.formatCurrency(total.toString())
      );
    }
    return this.$t("Confirm");
  }
  private async checkoutWithCreditCard() {
    if (
      Format.IsNull(this.name) ||
      Format.IsNull(this.cardnumber) ||
      Format.IsNull(this.expirationdate) ||
      Format.IsNull(this.securitycode)
    ) {
      this.error = this.$t("Please fill all fields").toString();
      return;
    }

    if (this.paymentMethod === undefined) {
      console.error("not payment method found for id " + this.paymentMethodId);
      return;
    }

    if (this.paymentMethod.type === PaymentMethodType.NetPay) {
      return this.netPay();
    }

    if (this.paymentMethod.type === PaymentMethodType.AuthorizeNet) {
      return await this.authorizeNet();
    }
  }

  public async checkout(fromCreditCardForm: boolean, customCheckoutParams: any = null) {
    let checkoutParams = customCheckoutParams ?? this.buildCheckoutParams();

    if (customCheckoutParams == null) {
      this.cleanErrors();
      this.checkErrors();

      if (!Format.IsNull(this.error)) {
        this.$store.commit("openGlobalError", this.error);
        return;
      }

      if (this.data?.isSharedPayment && this.paymentMethod?.paymentDeadline.isSharedPaymentAllowed === false) {
        const error = this.$t("This payment method does not allow shared payment due to deadline restrictions").toString();
        this.$store.commit("openGlobalError", error);
        return;
      }

      if (
        fromCreditCardForm === false &&
        this.tokenId == null &&
        (this.paymentMethod?.type === PaymentMethodType.NetPay ||
          this.paymentMethod?.type === PaymentMethodType.AuthorizeNet)
      ) {
        this.showCreditCardForm = true;
        return;
      }

      if (
        this.paymentMethod?.type == PaymentMethodType.Transbank &&
        this.tokenId == null
      ) {
        LocalStorage.add("checkoutParams", JSON.stringify(checkoutParams));
        this.transbankTokenize = true;
        return;
      }

      if(this.isSavingCard) {
        LocalStorage.add("checkoutParams", JSON.stringify(checkoutParams));
      }
    }

    let url =
      this.cid == 0
        ? `${Settings.HostName}/api/shop/checkout`
        : `${Settings.HostName}/api/shop/checkout-customer`;

    const data = await this.crudService.httpPost(url, checkoutParams);
    this.showCreditCardForm = false;
    const errorMsg =
      this.$t("Ups! Tecnical problems").toString() +
      ". " +
      this.$t("Please try again in a few minutes").toString();

    if (data == null || data === undefined) {
      console.error("Crash checkout " + this.cid);
      this.error = errorMsg;
      this.$store.commit("openGlobalError", this.error);
      return;
    }

    if (data.ok === false) {
      if (
        data.model !== null &&
        data.model.errorMsg.indexOf("netpay3ds-") !== -1
      ) {
        return (window.location.href =
          data.model.errorMsg.split(" netpay3ds-")[1]);
      }
      this.error = data.model.errorMsg !== "" ? data.model.errorMsg : errorMsg;
      this.$store.commit("openGlobalError", this.error);
      return;
    }

    if (data.model !== null && data.model.redsysData !== undefined) {
      this.redirectToRedsys(data.model.redsysData);
      return;
    }

    if (data.model !== null && data.model.razorpayData !== undefined) {
      this.openRazorpay(data.model.razorpayData);
      return;
    }

    if (data.model !== null && data.model.paypal !== undefined) {
      return data.model.paypal; // paypal create order
    }

    if (data.model !== null && data.model.stripe !== undefined) {
      return this.stripeSetUp(data.model.stripe, data.model.publicKey);
    }

    if (data.model !== null && data.model.bridgerPay !== undefined) {
      return this.bridgerPaySetUp(data.model.requestId, data.model.bridgerPay, data.model.publicKey);
    }

    if (data.model !== null && data.model.mercadoPago !== undefined) {
      return this.mercadoPago(
        data.model.mercadoPago.pk,
        data.model.mercadoPago.id,
        data.model.mercadoPago.amount,
        data.model.mercadoPago.email,
        data.model.mercadoPago.currency
      );
    }

    if (data.model !== null && data.model.transbank !== undefined) {
      this.redirectToTransbank(data.model.transbank);
      return;
    }

    if (data.ok === true) {
      this.shopService.removeCart();
      return this.$router.push("/cz/ok");
    }
  }

  private buildCheckoutParams(): CheckoutParams {
    return {
      saleItems: this.getSaleItems(),
      saleIds: this.$store.state.cart.sales.map((y) => y.saleIds).flat(),
      paymentMethodId: this.paymentMethodId,
      tokenId: this.tokenId,
      promoCode: this.discountInfo.promoCode,
      shopTransportId: Format.IsNull(this.transport) ? null : this.transport.id,
      saveCreditCard: this.saveCreditCard,
      guid: this.$store.state.cart.guid,
      tpvOnlineInfoTemp:
        this.netPayTokenInfo !== null
          ? JSON.stringify(this.netPayTokenInfo)
          : "",
      tpvOnlineNotPersitedInfo: JSON.stringify({
        card: this.cardnumber,
        exp: this.expirationdate,
      }),
      customer: {
        id: this.cid,
        name: this.data!.name,
        surname: this.data!.surname,
        addressLine1: this.data!.addressLine1,
        city: this.data!.city,
        town: this.data!.town,
        country: this.data!.country,
        postalCode: this.data!.postalCode,
        email: this.data!.email,
        phone: this.data!.phone,
        password: this.data!.password,
        okRss: this.okRss,
        acceptConditions: this.acceptConditions,
      },
      origin: Format.getOrigin(),
    };
  }

  private getSaleItems() {
    let saleItems = this.$store.state.cart.sItems.filter((y) => y.units > 0);
    if (this.discountInfo.promoCode === "") {
      return saleItems;
    }

    // one saleitem per line to calculate promos
    let saleItemsDto: any[] = [];
    for (const sItem of saleItems) {
      for (let j = 0; j < sItem.units; j++) {
        let unitOneSaleItem = Object.assign({}, sItem);
        unitOneSaleItem.units = 1;
        unitOneSaleItem.customerId = this.cid;
        unitOneSaleItem.buyerCustomerId = this.cid;
        saleItemsDto.push(unitOneSaleItem);
      }
    }
    return saleItemsDto;
  }

  private getSharedPaymentMessage() {
    if (this.data?.withRetention && this.paymentMethod) {
      return `${this.$t("We need your card as a guarantee, although you will not be charged anything until")} ${DateHelper.beautifyDate(this.paymentMethod.paymentDeadline.date)}. ${this.$t("Only the unpaid portion will be charged")}`;
    }
    return this.$t("We'll only charge your share, but as the reservation owner, you'll be responsible for covering any amount your teammates don't end up paying")
  }
  
  private cleanErrors() {
    this.error = "";
  }

  private checkErrors(skipPaymenMethod: boolean = false) {
    this.error = Format.IsNull(this.error) ? "" : this.error;

    if (this.$store.state.cart.count === 0) {
      this.error += "<br>" + this.$t("Your basket is empty!");
    }
    
    if (!skipPaymenMethod) {
      const error = this.getRequiredPaymentMethodError();
      if(error) {
        this.error += "<br>" + error;
      }
    }

    if (this.acceptConditions === false) {
      this.error +=
        "<br>" + this.$t("Please accept our legal conditions").toString();
    }
  }

  private getRequiredPaymentMethodError() {
    if(this.paymentMethod) {
      return null;
    }
    if (this.data?.isSharedPayment) {

      return this.requiresToSaveNewCard
        ? this.$t("Please select a payment method, we need it as a guarantee for any amount your teammates may not cover").toString()
        : this.$t("Please select a saved card, we need it as a guarantee for any amount your teammates may not cover").toString()    
    }
    if(this.requiresPaymentMethod) {
      return this.$t("Please select a payment method").toString();
    }
    return null;
  }
}
